import React, { Component } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import { find, isEmpty, isNil, propEq } from 'ramda';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Pagination from '@components/@core/Pagination';
import Filter from '@components/@core/Filter';
import DatePicker from '@components/@core/DatePicker';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import ToggleColumnMenu from '@components/@core/ToggleColumnMenu';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { UserRoles, ReportMenu } from '~/services/config';
import { exportCsv } from '~/services/util-excel';
import withNavigate from '~/hocs/withNavigate';

const PdfIcon = loadable(() => import('./SchedulePdf'), { ssr: false });
const moment = extendMoment(originalMoment);

class CompanyTrainingSchedule extends Component {
    state = {
        userId: this.props.userId,
        company: this.props.company,
        dateStart: moment().startOf('month').format('YYYY-MM-DD'),
        dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
        page: 1,
        perPage: 25,
        column: 1,
        showPrint: false,
    };

    componentDidMount() {
        const { dateStart, dateEnd, page, perPage } = this.state;
        const payload = {
            page,
            per_page: perPage,
            date_range_start: dateStart,
            date_range_end: dateEnd,
        };
        this.props.getTrainingSchedule(payload);
    }

    handleFilter = (type, e) => {
        if (type === 'assignee') {
            this.setState({ userId: e[0].id });
        } else {
            this.setState({ company: e[0] });
        }
    };

    handleDate = (e) => {
        const { perPage } = this.state;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        const payload = {
            date_range_start: dateStart,
            date_range_end: dateEnd,
            page: 1,
            per_page: perPage,
        };
        this.props.getTrainingSchedule(payload);
        this.setState({ dateStart, dateEnd });
    };

    handlePagination = (page) => {
        const { perPage, dateStart, dateEnd } = this.state;
        const payload = {
            date_range_start: dateStart,
            date_range_end: dateEnd,
            page,
            per_page: perPage,
        };
        this.props.getTrainingSchedule(payload);
    };

    handlePer = (perPage) => {
        const { page, dateStart, dateEnd } = this.state;
        const payload = {
            date_range_start: dateStart,
            date_range_end: dateEnd,
            page,
            per_page: perPage,
        };
        this.props.getTrainingSchedule(payload);
        this.setState({ perPage });
    };

    handleOpenDetail = (e, editable = false) => {
        if (e) {
            this.props.navigate({
                pathname: `/hcm/report-training-schedule/${e.id}`,
                state: { editable },
            });
        } else {
            this.props.navigate(`/hcm/report-training-schedule/null`);
        }
    };

    handleChange = (schedule, event) => {
        switch (event) {
            case 'detail view':
                this.handleOpenDetail(schedule, false);
                break;

            case 'edit':
                this.handleOpenDetail(schedule, true);
                break;

            default:
                break;
        }
    };

    handleVisible = (column) => {
        this.setState({ column });
    };

    handleExcelReport = () => {
        const { trainingSchedules } = this.props;
        this.handlePer(trainingSchedules.total);
        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { trainingSchedules, employees } = this.props;
        const { data } = trainingSchedules;
        let excelData = [];
        data.map((schedule) => {
            const user = find(propEq('id', schedule.user_id), employees) || {};
            const scheduleData = {
                Schedule: schedule.title,
                Manager: user.name,
                Started: isNil(schedule.start_at) ? 'N/A' : moment(schedule.start_at).format('MMM D'),
                'End Date': isNil(schedule.end_at) ? 'N/A' : moment(schedule.end_at).format('MMM D'),
                Assigned: schedule.stats.assigned,
            };
            excelData.push(scheduleData);
        });

        const key = ['Schedule', 'Manager', 'Started', 'End Date', 'Assigned'];
        exportCsv(excelData, key, 'Reports-Training Schedule', true);
        this.handlePer(25);
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.setState({ showPrint: false });
                this.handleExcelReport();
                break;
            case 'print':
                this.setState({ showPrint: true });
                break;
        }
    };

    render() {
        const { isManager, trainingSchedules, employees } = this.props;
        const { current_page, last_page, per_page, data } = trainingSchedules;
        const { dateStart, dateEnd } = this.state;
        const date = moment.range(dateStart, dateEnd);
        const dotsMenu = isManager ? ['Detail View', 'Edit'] : ['Detail View'];

        return (
            <ErrorBoundary className="report-training-schedule">
                <Filter permission={UserRoles.USER} addPrefix="Assign" addTitle="Training Schedule" onAdd={() => this.handleOpenDetail()} onChange={this.handleFilter} />
                <div className="training-schedule-list">
                    <div className="d-h-between">
                        <p className="dsl-b22 bold">Learning Schedule Report</p>
                        <div className="d-flex">
                            <DatePicker
                                calendar="range"
                                append="caret"
                                format="MMM D"
                                as="span"
                                align="right"
                                dataCy="hcm-report-trainingScheduleFilterByDate"
                                value={date}
                                closeAfterSelect
                                onSelect={this.handleDate}
                            />
                            <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                            {this.state.showPrint && <PdfIcon state={this.state} props={this.props} name="Print" />}
                        </div>
                    </div>
                    <div className="d-flex border-bottom pt-3 pb-3 desktop-screen">
                        <div className="d-flex-5 dsl-m12">Schedule</div>
                        <div className="d-flex-3 d-flex-ssm-4 dsl-m12 text-start">Manager</div>
                        <div className="d-flex-2 dsl-m12 text-end">Started</div>
                        <div className="d-flex-2 dsl-m12 text-end">End date</div>
                        <div className="d-flex-2 dsl-m12 text-end">Assigned</div>
                        <div className="d-flex-1 dsl-m12" />
                    </div>
                    {isEmpty(data) ? (
                        <div className="d-h-center pt-4">
                            <span className="dsl-m16">No Training Schedule Assigned</span>
                        </div>
                    ) : (
                        data.map((schedule, index) => {
                            const user = find(propEq('id', schedule.user_id), employees) || {};
                            const started = isNil(schedule.start_at) ? 'N/A' : moment(schedule.start_at).format('MMM D');
                            const ended = isNil(schedule.end_at) ? 'N/A' : moment(schedule.end_at).format('MMM D');
                            return (
                                <div className="cursor-pointer d-flex ssm-column border-bottom list-items" key={schedule.id}>
                                    <div className="d-flex-5 d-flex jc-between mb-0 ssm-b-2">
                                        <div className="dsl-b14 text-400" onClick={() => this.handleOpenDetail(schedule, false)}>
                                            {schedule.title}
                                        </div>
                                        <EditDropdown className="mobile-screen" options={dotsMenu} onChange={(e) => this.handleChange(schedule, e)} />
                                    </div>
                                    <div className="d-flex-3 d-flex-ssm-4 dsl-b14 text-400 text-start">
                                        <label className="dsl-b14 mobile-screen mb-0 me-3 w-40">Manager</label>
                                        {user.name}
                                    </div>
                                    <div className="d-flex-2 dsl-b14 text-400 text-end ssm-left pt-0 ssp-t-2">
                                        <label className="dsl-b14 mobile-screen mb-0 me-3 w-40">Started</label>
                                        {started}
                                    </div>
                                    <div className="d-flex-2 dsl-b14 text-400 text-end ssm-left pt-0 ssp-t-2">
                                        <label className="dsl-b14 mobile-screen mb-0 me-3 w-40">Ended</label>
                                        {ended}
                                    </div>
                                    <div className="d-flex-2 dsl-b14 text-400 text-end ssm-left pt-0 ssp-t-2">
                                        <label className="dsl-b14 mobile-screen me-3 w-40">Assigned</label>
                                        {schedule.stats.assigned}
                                    </div>
                                    <div className="d-flex-1 jc-end pt-0 ssp-t-2 desktop-screen">
                                        <EditDropdown options={dotsMenu} onChange={(e) => this.handleChange(schedule, e)} />
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <Pagination current={current_page} total={last_page} onChange={this.handlePagination} onPer={this.handlePer} />
                </div>
            </ErrorBoundary>
        );
    }
}

CompanyTrainingSchedule.propTypes = {
    userId: PropTypes.number,
    isAdmin: PropTypes.bool,
    isManager: PropTypes.bool,
    company: PropTypes.shape({
        id: PropTypes.number,
    }),
    trainingSchedules: PropTypes.shape({
        current_page: PropTypes.number,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        data: PropTypes.array,
    }),
    employees: PropTypes.array,
    getTrainingSchedule: PropTypes.func,
    fetchTemplates: PropTypes.func,
};

CompanyTrainingSchedule.defaultProps = {
    userId: 0,
    isAdmin: false,
    isManager: false,
    company: { id: 0 },
    trainingSchedules: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        data: [],
        total: 1,
    },
    employees: [],
    getTrainingSchedule: () => {},
    fetchTemplates: () => {},
    toggleModal: () => {},
};

const mapStateToProps = (state) => ({
    userId: state.app.id,
    isAdmin: state.app.app_role_id === 2,
    isManager: state.app.app_role_id < UserRoles.USER,
    company: state.app.company_info,
    employees: state.app.users,
    trainingSchedules: state.develop.trainingSchedules,
});

const mapDispatchToProps = (dispatch) => ({
    getTrainingSchedule: (e) => dispatch(DevelopActions.traininglistRequest(e)),
    fetchTemplates: (e) => dispatch(DevelopActions.librarycardtemplatesRequest(e)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const CompanyTrainingScheduleWithNavigate = withNavigate(CompanyTrainingSchedule);

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTrainingScheduleWithNavigate);
